/*
=============================================
   EXPORT GRADES  (professor portal view)
=============================================
Pick a class, see the grid, download the CSV.

This file was previously ZERO BYTES. <app-export> was declared in professor.html
and admin.html, so the menu item worked and the view opened, and it rendered an
empty element with no error anywhere. That failure mode is worth naming: an
undefined custom element is silently valid HTML, so "not built yet" and "broken"
look identical from the outside.

WHAT IS AND IS NOT IN THE FILE. Correctness/points and challenge best-times sit
side by side, blank where they do not apply, because the platform grades three
different kinds of thing and averaging them into one number is a teaching
decision rather than a reporting one. Engagement minutes are NOT here: they have
their own export, computed from the raw event stream by lib/engagement.js, and a
second implementation of the number FIN 397 grades 80% on would eventually
disagree with the first. The panel says so rather than leaving you to wonder.
*/

import { api } from "../scripts/api.js";

const SECS = (v) => {
   if (v === null || v === undefined || v === "") return "";
   const n = Number(v);
   if (!Number.isFinite(n)) return "";
   return n < 60 ? `${Math.round(n)}s`
      : `${Math.floor(n / 60)}:${String(Math.round(n % 60)).padStart(2, "0")}`;
};

const esc = (t) => String(t ?? "").replace(/[<>&]/g, "");

/*
A dropdown of checkboxes with Select all / none.

A native <select multiple> was the obvious first thought and is wrong here: it
needs ctrl-click to pick more than one, shows about four rows at a time, and gives
no way to say "all". Professors pick "everything" or "these three", and both should
be one click.

The button label always states the current selection, so a filtered export is never
a surprise. "All" is a real state rather than "every box happens to be ticked": it
sends no parameter at all, which the server reads as unfiltered, and it keeps
working when someone adds an assignment to the class later.
*/
// EXPORTED 2026-09-12: the Scores view uses the same three filters, and two
// copies of a filter control is how they drift apart.
export class Picker {
   constructor(host, label, onChange) {
      this.host = host;
      this.label = label;
      this.onChange = onChange;
      this.items = [];
      this.selected = null;              // null = ALL
      host.classList.add("pick");
      host.innerHTML = `
         <button type="button" class="pick-btn"></button>
         <div class="pick-menu" hidden>
            <div class="pick-actions">
               <button type="button" data-all="1">Select all</button>
               <button type="button" data-none="1">Clear</button>
            </div>
            <div class="pick-list"></div>
         </div>`;
      this.btn = host.querySelector(".pick-btn");
      this.menu = host.querySelector(".pick-menu");
      this.list = host.querySelector(".pick-list");

      this.btn.addEventListener("click", (e) => {
         e.stopPropagation();
         this.menu.hidden = !this.menu.hidden;
      });
      this.menu.addEventListener("click", (e) => e.stopPropagation());
      // Close on an outside click, the behaviour every other dropdown on the web has.
      document.addEventListener("click", () => { this.menu.hidden = true; });

      host.querySelector("[data-all]").addEventListener("click", () => {
         this.selected = null;
         this.sync();
         this.onChange();
      });
      host.querySelector("[data-none]").addEventListener("click", () => {
         this.selected = new Set();
         this.sync();
         this.onChange();
      });
   }

   setItems(items) {                     // [{ id, label, note }]
      this.items = items;
      this.selected = null;
      this.list.innerHTML = items.map((it) => `
         <label class="pick-row">
            <input type="checkbox" value="${it.id}" checked />
            <span>${esc(it.label)}</span>
            ${it.note ? `<span class="pick-note">${esc(it.note)}</span>` : ""}
         </label>`).join("");
      this.list.querySelectorAll("input").forEach((cb) => {
         cb.addEventListener("change", () => {
            const ticked = [...this.list.querySelectorAll("input")]
               .filter((x) => x.checked).map((x) => Number(x.value));
            // Everything ticked collapses back to ALL, so adding an assignment
            // later is included rather than silently excluded by a stale id list.
            this.selected = ticked.length === this.items.length ? null : new Set(ticked);
            this.syncLabel();
            this.onChange();
         });
      });
      this.syncLabel();
   }

   sync() {
      const all = this.selected === null;
      this.list.querySelectorAll("input").forEach((cb) => {
         cb.checked = all || this.selected.has(Number(cb.value));
      });
      this.syncLabel();
   }

   syncLabel() {
      const n = this.selected === null ? this.items.length : this.selected.size;
      const what = this.selected === null
         ? `All ${this.label}`
         : n === 0 ? `No ${this.label}`
         : n === 1 ? [...this.selected].map((id) =>
              (this.items.find((i) => i.id === id) || {}).label).join("")
         : `${n} ${this.label}`;
      this.btn.textContent = `${what} ▾`;
   }

   // The query value: undefined when unfiltered, so the parameter is omitted.
   param() {
      if (this.selected === null) return undefined;
      return [...this.selected].join(",");
   }
}

class AppExport extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <h1 class="page-title">Export Grades</h1>
         <p class="page-sub">One row per student per assignment. Correctness and points
            for graded assignments, best time and attempts for timed challenges, blank
            where a column does not apply to that kind of work.</p>

         <div class="ex-bar">
            <label>Class <select id="ex-class"></select></label>
            <label>Assignments <span id="ex-assign"></span></label>
            <label>Students <span id="ex-students"></span></label>
            <button id="ex-csv" class="ex-btn" disabled>Download CSV</button>
         </div>

         <p class="ex-note">Active minutes are not in this file. They have their own
            export under <b>Engagement</b>, computed from the raw event stream.</p>

         <div id="ex-status" class="ex-status"></div>
         <div class="ex-table-wrap" hidden>
            <table class="ex-table"><thead id="ex-head"></thead><tbody id="ex-body"></tbody></table>
         </div>`;

      this.classSel = this.querySelector("#ex-class");
      // Changing class resets both pickers, because an assignment id from the
      // previous class means nothing here and would filter everything away.
      this.classSel.addEventListener("change", () => this.load({ resetPickers: true }));
      this.assignPick = new Picker(this.querySelector("#ex-assign"), "assignments",
                                   () => this.load());
      this.studentPick = new Picker(this.querySelector("#ex-students"), "students",
                                    () => this.load());
      this.querySelector("#ex-csv").addEventListener("click", () => this.downloadCsv());
      this.loadClasses();
   }

   // Called by the router when the view is shown.
   show() {
      if (this.classSel && this.classSel.value) this.load();
   }

   status(text, kind = "") {
      const el = this.querySelector("#ex-status");
      el.textContent = text || "";
      el.className = "ex-status " + kind;
   }

   async loadClasses() {
      try {
         const r = await api("/list-classes");
         if (!r.ok) throw new Error(`list-classes ${r.status}`);
         const data = await r.json();
         const classes = data.classes ?? [];
         if (!classes.length) {
            this.status("No classes yet.");
            return;
         }
         this.classSel.innerHTML = classes.map((c) =>
            `<option value="${c.class_id}">${esc(c.course_code)} · ${esc(c.section)} — `
            + `${esc(c.title || "")}</option>`).join("");
         this.load({ resetPickers: true });
      } catch (err) {
         this.status("Could not load your classes.", "err");
      }
   }

   query(extra = {}) {
      const p = new URLSearchParams({ class_id: this.classSel.value, ...extra });
      const a = this.assignPick && this.assignPick.param();
      const s = this.studentPick && this.studentPick.param();
      if (a !== undefined) p.set("assignments", a);
      if (s !== undefined) p.set("students", s);
      return p.toString();
   }

   async load({ resetPickers = false } = {}) {
      const classId = this.classSel.value;
      if (!classId) return;
      this.status("Loading…");
      this.querySelector("#ex-csv").disabled = true;
      try {
         const qs = resetPickers
            ? new URLSearchParams({ class_id: classId }).toString()
            : this.query();
         const r = await api(`/export/grades?${qs}`);
         if (r.status === 403) { this.status("You do not teach that class.", "err"); return; }
         if (!r.ok) throw new Error(`export ${r.status}`);
         const data = await r.json();
         // Options come back unfiltered, so repopulating them here cannot narrow
         // the picker to the current selection and trap you in it.
         if (resetPickers || !this.assignPick.items.length) {
            this.assignPick.setItems((data.assignments ?? []).map((a) => ({
               id: a.assignment_id,
               label: a.title,
               note: a.archetype === "timed_challenge" ? "challenge" : "",
            })));
            this.studentPick.setItems((data.students ?? []).map((s) => ({
               id: s.user_id, label: s.student, note: "",
            })));
         }
         this.render(data.rows ?? []);
      } catch (err) {
         this.status("Could not load grades.", "err");
         console.error("export load failed", err);
      }
   }

   render(rows) {
      const wrap = this.querySelector(".ex-table-wrap");
      if (!rows.length) {
         wrap.hidden = true;
         // Distinguish "you filtered everything out" from "there is nothing here",
         // because the fix is completely different.
         const filtered = this.assignPick.param() !== undefined
                       || this.studentPick.param() !== undefined;
         this.status(filtered
            ? "No rows match the current filters."
            : "Nothing to export: this class has no assignments attached, or no "
              + "students enrolled.");
         return;
      }
      this.status("");
      wrap.hidden = false;
      this.querySelector("#ex-csv").disabled = false;

      this.querySelector("#ex-head").innerHTML =
         "<tr><th>Student</th><th>Assignment</th><th>Type</th><th>Correct</th>"
         + "<th>Points</th><th>Best time</th><th>Attempts</th><th>Last activity</th></tr>";

      this.querySelector("#ex-body").innerHTML = rows.map((r) => {
         const timed = r.type === "timed_challenge";
         const correct = (r.correct === null || r.correct === undefined)
            ? "" : `${r.correct} / ${r.out_of ?? "?"}`;
         const points = (r.points_earned === null || r.points_earned === undefined)
            ? "" : `${r.points_earned} / ${r.points_total ?? "?"}`;
         const when = r.last_activity
            ? new Date(r.last_activity).toLocaleDateString(undefined,
               { month: "short", day: "numeric" })
            : "";
         return `<tr>
            <td>${esc(r.student)}</td>
            <td>${esc(r.assignment)}</td>
            <td>${timed ? "challenge" : "graded"}</td>
            <td>${timed ? "" : correct}</td>
            <td>${timed ? "" : points}</td>
            <td>${timed ? SECS(r.best_time_secs) : ""}</td>
            <td>${timed ? (r.attempts ?? "") : ""}</td>
            <td class="muted">${when}</td>
         </tr>`;
      }).join("");
   }

   /*
   Download via a normal navigation rather than fetch-then-blob.

   The route already sets Content-Disposition, and the session cookie rides along
   on a same-site top-level request, so the browser saves the file with the right
   name and nothing has to be re-implemented client-side.
   */
   downloadCsv() {
      const classId = this.classSel.value;
      if (!classId) return;
      const base = (location.hostname === "localhost")
         ? "https://localhost:3001" : "https://api.gridprof.com";
      window.location.href = `${base}/export/grades?${this.query({ format: "csv" })}`;
   }
}

customElements.define("app-export", AppExport);
